import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Heart, Shield, Users, HandHeart, ArrowLeft } from 'lucide-react';

export default function AboutPage() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="glass-header fixed top-0 left-0 right-0 z-50 border-b border-[#E6E2D6]/50">
        <div className="px-6 md:px-12 lg:px-24 py-4 flex items-center justify-between">
          <Link to="/" className="font-heading text-2xl font-semibold text-[#2B2E26]" data-testid="logo">
            Pray Together
          </Link>
          
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-[#5C6056] hover:text-[#2B2E26] transition-colors font-body">
              Home
            </Link>
            <Link to="/about" className="text-[#5A6E4B] font-medium font-body">
              About
            </Link>
            {user ? (
              <Link to="/dashboard" className="btn-primary text-sm py-3 px-6" data-testid="dashboard-link">
                My Dashboard
              </Link>
            ) : (
              <Link to="/register" className="btn-primary text-sm py-3 px-6" data-testid="become-warrior-btn">
                Become a Warrior
              </Link>
            )}
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section 
        className="pt-32 pb-16 px-6 md:px-12 lg:px-24 relative min-h-[60vh] flex items-center"
        style={{
          backgroundImage: `url(https://images.unsplash.com/photo-1555098621-bfb98f5880ad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxlbXB0eSUyMHRvbWIlMjByZXN1cnJlY3Rpb24lMjBzdW5yaXNlfGVufDB8fHx8MTc3NTM1MjE4MXww&ixlib=rb-4.1.0&q=85)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-[#FDFBF7]/75"></div>
        <div className="max-w-4xl mx-auto relative z-10">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-[#5C6056] hover:text-[#2B2E26] transition-colors mb-8 font-body"
            data-testid="back-home"
          >
            <ArrowLeft size={18} />
            Back to Home
          </Link>
          
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-heading text-4xl sm:text-5xl font-medium text-[#2B2E26] mb-6"
            data-testid="about-title"
          >
            About Pray Together
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-body text-lg text-[#5C6056] leading-relaxed"
          >
            A community of faith, united in prayer for one another.
          </motion.p>
        </div>
      </section>

      {/* Our Commitment */}
      <section className="py-16 px-6 md:px-12 lg:px-24 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-[#5A6E4B]/10 flex items-center justify-center">
                <Heart className="w-6 h-6 text-[#5A6E4B]" />
              </div>
              <h2 className="font-heading text-2xl sm:text-3xl font-medium text-[#2B2E26]">
                Our Commitment
              </h2>
            </div>
            
            <div className="space-y-4 font-body text-[#5C6056] leading-relaxed text-lg">
              <p>
                <strong className="text-[#2B2E26]">Pray Together is 100% free and will never be operated for profit.</strong>
              </p>
              <p>
                This service exists solely to connect those in need of prayer with faithful prayer warriors willing to lift them up before our Heavenly Father. We believe that prayer is a sacred gift, freely given, and should never be commercialized.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Purpose of Prayer */}
      <section className="py-16 px-6 md:px-12 lg:px-24 bg-[#F4F1E9]">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-[#C86A51]/10 flex items-center justify-center">
                <HandHeart className="w-6 h-6 text-[#C86A51]" />
              </div>
              <h2 className="font-heading text-2xl sm:text-3xl font-medium text-[#2B2E26]">
                The Purpose of Prayer
              </h2>
            </div>
            
            <div className="bg-white/80 backdrop-blur-sm p-8 rounded-2xl border border-[#E6E2D6]">
              <p className="font-body text-[#5C6056] leading-relaxed text-lg">
                God is our living Heavenly Father. The intent of prayer is not to change God's will, but to align our will to His and ask for blessings that He is willing to give us based upon our faithfulness.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Privacy & Anonymity */}
      <section className="py-16 px-6 md:px-12 lg:px-24 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-[#5A6E4B]/10 flex items-center justify-center">
                <Shield className="w-6 h-6 text-[#5A6E4B]" />
              </div>
              <h2 className="font-heading text-2xl sm:text-3xl font-medium text-[#2B2E26]">
                Privacy & Anonymity
              </h2>
            </div>
            
            <div className="space-y-4 font-body text-[#5C6056] leading-relaxed text-lg">
              <p>
                <strong className="text-[#2B2E26]">No personally identifiable information should be submitted.</strong> Please use only first names when submitting prayer requests.
              </p>
              <p>
                Any information provided should maintain the anonymity of both the submitter and the person being prayed for. This protects everyone's privacy while still allowing our community to lift up meaningful prayers on their behalf.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Guidelines for Prayer Requests */}
      <section className="py-16 px-6 md:px-12 lg:px-24 bg-[#F4F1E9]">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-[#C86A51]/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-[#C86A51]" />
              </div>
              <h2 className="font-heading text-2xl sm:text-3xl font-medium text-[#2B2E26]">
                Guidelines for Prayer Requests
              </h2>
            </div>
            
            <div className="space-y-6 font-body text-[#5C6056] leading-relaxed text-lg">
              <p>
                Requests for prayers should be made in good faith, adhering to Christ-like principles of:
              </p>
              
              <ul className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {['Faith', 'Patience', 'Kindness', 'Charity', 'Love'].map((principle, index) => (
                  <motion.li
                    key={principle}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white p-4 rounded-xl text-center border border-[#E6E2D6]"
                  >
                    <span className="font-heading text-lg text-[#5A6E4B]">{principle}</span>
                  </motion.li>
                ))}
              </ul>
              
              <p>
                These principles reflect our love for all of God's children.
              </p>
              
              <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl border border-[#E6E2D6]">
                <p className="font-medium text-[#2B2E26] mb-3">Respecting Free Agency</p>
                <p>
                  Prayer requests should respect the free agency that God has given to man. Requests should not ask for any acts of violence, harm, or anything that would infringe upon another person's God-given agency to choose their own path.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#2B2E26]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-medium text-[#F4F1E9] mb-6">
              Join Our Community of Prayer
            </h2>
            <p className="font-body text-[#8A8D86] mb-8 max-w-2xl mx-auto">
              Whether you're seeking prayers for a loved one or ready to serve as a prayer warrior, 
              we welcome you with open arms.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/#submit" className="btn-accent" data-testid="submit-prayer-cta">
                Submit a Prayer Request
              </Link>
              {!user && (
                <Link to="/register" className="btn-secondary" data-testid="join-warrior-cta">
                  Become a Prayer Warrior
                </Link>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#2B2E26] py-12 border-t border-[#5C6056]/20">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="text-center md:text-left">
              <p className="font-heading text-xl text-[#F4F1E9] mb-1">Pray Together</p>
              <p className="font-body text-[#8A8D86] text-sm">Bear one another's burdens — Galatians 6:2</p>
            </div>
            <div className="flex gap-6 text-[#8A8D86] font-body text-sm">
              <Link to="/" className="hover:text-[#F4F1E9] transition-colors">Home</Link>
              <Link to="/about" className="hover:text-[#F4F1E9] transition-colors">About</Link>
            </div>
          </div>
          <div className="border-t border-[#5C6056]/20 mt-6 pt-6 text-center">
            <p className="text-[#8A8D86] text-xs font-body">
              © {new Date().getFullYear()} Pray Together. Free forever, for the glory of God.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
